import { useEffect, useMemo, useState } from "react";
import { useMatch, useLocation, useNavigate } from "react-router-dom";

// formik components
import { Formik, Form } from "formik";

// Auth0
import { useAuth0 } from "@auth0/auth0-react";

// PropTypes
import PropTypes from "prop-types";

// Global controller
import { useSoftUIController } from "hooks/context";

// @mui material components
import { Card, Stepper, Step, StepLabel, CircularProgress } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

import Swal from "sweetalert2";
import withReactContent from 'sweetalert2-react-content';

// NewUser layout schemas for form and form feilds
import form from "./schemas/form";
import validationSchema from "./schemas/validationSchema";
import defaultServiceValues from "./schemas/initialValues";

import DeviceSelection from "./wizards/DeviceSelection";
import TechnicianActions from "./wizards/TechnicianActions";
import AdditionalNotes from "./wizards/AdditionalNotes";
import Review from "./wizards/Review";
import DeviceConditions from "./wizards/DeviceConditions";
import moment from "moment";
import imageCompression from 'browser-image-compression';
import async from 'async';

import bksAPI from "services/api/bks";
import { useQuery } from "@tanstack/react-query";
import { updateProgressUI } from "modules/inspections/utils/submission";
import { renderContent } from "modules/inspections/utils/submission";
import { processDeviceData } from "utils/device";
import { determineInspectionStatus } from "utils/inspection";

function getStepContent(stepIndex, formData, deviceLayout, userPermissions) {
    switch (stepIndex) {
        case 0:
            return <DeviceSelection {...{ formData, deviceLayout, userPermissions }} />
        case 1:
            return <DeviceConditions {...{ formData, deviceLayout, userPermissions }} />;
        case 2:
            return <TechnicianActions {...{ formData, deviceLayout, userPermissions }} />;
        case 3:
            return <AdditionalNotes {...{ formData, deviceLayout, userPermissions }} />;
        // case 4:
        //     return <Review {...{formData, deviceLayout}} />;
        default:
            return null;
    }
}

function InspectionsForm({ selected_user, all_devices, maintenance_tags, selected_device, onSubmit }) {
    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    bksAPI.loadToken(useAuth0());

    const [deviceOptions, setDeviceOptions] = useState([]);
    const [fetchedUuids, setFetchedUuids] = useState(typeof selected_device?.uuid != 'undefined' ? [selected_device.uuid] : []);
    const [maintenanceOptions] = useState(maintenance_tags.filter(t => t.status != 'complete').map(m => { return { label: m.tag_label, value: m.tag_label } }));

    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Device Selection", "Conditions", "Maintenance/Repairs", "Notes & Images"];
    const isLastStep = activeStep === steps.length - 1;

    const { formId, formField } = form;
    const currentValidation = validationSchema[activeStep] || Yup.object().shape({});

    const { data: fetchedData } = useQuery({
        queryKey: ['getDevices', fetchedUuids.join(','), selectedOrg],
        enabled: fetchedUuids.length > 0,
        queryFn: async () => await bksAPI.getDevices(fetchedUuids),
        refetchInterval: 5 * 1000
    });

    const fetchDevices = {};
    if (typeof fetchedData != 'undefined') {
        for (var d of fetchedData) {
            if (typeof fetchDevices[d.uuid] == 'undefined') fetchDevices[d.uuid] = processDeviceData(moment().unix() * 1000, d);
        }
    }

    const handleSubmit = (values, actions) => {
        // console.log(values, actions);
        if (isLastStep) {
            submitForm(values, actions);
        } else {
            setActiveStep(activeStep + 1);
            actions.setTouched({});
            actions.setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    function getObjValues(values, label) {
        var ret = {};
        var label_values = Object.keys(values).filter((row) => row.includes(label));

        for (var el of label_values) {
            var element_arr = el.split('-');
            var type = element_arr[(element_arr.length - 1)];
            ret[type] = values[`${label}-${type}`];
        }

        return ret;
    }

    const submitForm = async (values, actions) => {
        const apiPayload = new FormData();
        const ReactSwal = withReactContent(Swal);

        let metadataObj = {
            title: values.title == "" ? values.inspection_type.trim() : values.title.trim(),
            description: values.description.trim(),
            maintenance_tag: values.maintenance_tag == "" ? undefined : values.maintenance_tag,
            organisation_id: values.organisation_id,
            recordedAt: values.recordedAt == "" ? undefined : new Date(values.recordedAt).toISOString().replace('Z', '+00:00'),
            priority: values.priority != "" ? values.priority : "low",
            location: values.location,
            devices: [],
            technician: selected_user.user.nickname,
        };

        const deviceLabels = ['devicePrimary', 'deviceSecondary'];
        const flatImagesQueue = [];

        // 1. Build initial metadata and flatten images into a single queue for the async limiter
        deviceLabels.forEach((deviceLabel, deviceIndex) => {
            if (values[`${deviceLabel}-info`] != "") {
                metadataObj.devices[deviceIndex] = {
                    uuid: values[`${deviceLabel}-uuid`],
                    name: values[`${deviceLabel}-info`].value.name,
                    serial_no: values[`${deviceLabel}-serial_no`],
                    stop_id: values[`${deviceLabel}-stop_id`],
                    conditions: getObjValues(values, `${deviceLabel}-conditions`),
                    actions: getObjValues(values, `${deviceLabel}-actions`),
                    images: [] // Will be populated after compression
                };

                const imagesArray = values[`${deviceLabel}-images`] || [];
                imagesArray.forEach((image, imageIndex) => {
                    flatImagesQueue.push({
                        deviceIndex,
                        key: `device${deviceIndex}_image${imageIndex}`,
                        file: image.file
                    });
                });
            }
        });

        const totalImages = flatImagesQueue.length;
        let compressedCount = 0;

        ReactSwal.fire({
            icon: "info",
            title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:24px">Preparing Submission</div>`,
            html: renderContent(),
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                updateProgressUI('compressing', 0, totalImages);
            },
            width: deviceLayout == "mobile" ? '20rem' : '32rem'
        });

        try {
            // 2. Process EXACTLY 3 images at a time. Protects RAM
            const compressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };

            const compressedResults = await async.mapLimit(flatImagesQueue, 3, async (item) => {
                const compressedBlob = await imageCompression(item.file, compressionOptions);
                const compressedFile = new File([compressedBlob], item.file.file || item.file.name, { type: compressedBlob.type });

                compressedCount++;
                updateProgressUI('compressing', compressedCount, totalImages);

                return {
                    deviceIndex: item.deviceIndex,
                    key: item.key,
                    fileName: compressedFile.name,
                    compressedFile: compressedFile
                };
            });

            // 3. Reassemble the payload
            compressedResults.forEach((result) => {
                apiPayload.append(result.key, result.compressedFile);
                metadataObj.devices[result.deviceIndex].images.push({
                    key: result.key,
                    fileName: result.fileName
                });
            });

            metadataObj.status = determineInspectionStatus("inspection", metadataObj);

            // 4. Transition to Upload Phase
            const titleEl = Swal.getTitle();
            if (titleEl) titleEl.innerHTML = `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:24px">Uploading...</div>`;

            const handleUploadProgress = (percent) => {
                updateProgressUI('uploading', totalImages, totalImages, percent);
            };

            await onSubmit([metadataObj, apiPayload, handleUploadProgress]);

            await Swal.fire({
                icon: "success",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                showConfirmButton: false,
                timer: 2000,
                width: deviceLayout == "mobile" ? '20rem' : '32rem'
            });

            navigate(-1);

        } catch (err) {
            console.error("Submission failed:", err);
            actions.setSubmitting(false);

            await Swal.fire({
                icon: "error",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${err.message || "Something went wrong while saving."}</div>`,
                showConfirmButton: true,
                timer: 15000,
                width: deviceLayout == "mobile" ? '20rem' : '32rem'
            });
        }
    }

    const formInitialValues = useMemo(() => {
        if (!selected_device) {
            setActiveStep(0);
            return defaultServiceValues;
        }

        setActiveStep(1);
        const primary = selected_device;
        let initial = { ...defaultServiceValues };

        // Map Basic Info
        initial['devicePrimary-uuid'] = primary.uuid || "";
        initial['devicePrimary-serial_no'] = primary.serial_number || "";
        initial['devicePrimary-stop_id'] = primary?.stop?.stop_id || primary?.stop?.site_id || "";
        initial['devicePrimary-info'] = { value: { ...primary }, metadata: { ...primary }, label: primary.name };

        initial['organisation_id'] = primary.organisation_id || "";
        initial['location'] = [primary.position[1] || 0, primary.position[0] || 0];

        initial[`devicePrimary-actions-installed`] = false;

        return initial;
    }, [selected_device]);

    return (
        <SoftBox height={deviceLayout == "mobile" ? "calc(100% - 16px)" : "calc(100% - 2rem)"} px={1} pb={1} py={deviceLayout == "mobile" ? 1 : "1rem"}>
            {deviceLayout != "mobile" && (
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}
            <Formik
                initialValues={formInitialValues}
                validationSchema={currentValidation}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}>
                {({ values, errors, touched, isSubmitting, setFieldValue, handleChange, resetForm }) => {
                    Object.keys(errors).length > 0 && console.log("Validation Errors:", errors);

                    return (
                        <Form id={formId} autoComplete="off" style={{ height: deviceLayout == "mobile" ? '100%' : 'unset' }}>
                            <Card sx={{ overflow: "visible", height: '100%' }}>
                                <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="space-between">
                                    {getStepContent(activeStep, {
                                        values,
                                        touched,
                                        errors,
                                        isSubmitting,
                                        formField,
                                        resetForm,
                                        functions: {
                                            setDeviceOptions,
                                            setFetchedUuids,
                                            setFieldValue,
                                            handleChange,
                                            handleBack,
                                            handleCancel
                                        },
                                        custom: {
                                            fetchedUuids,
                                            fetchDevices,
                                            activeStep,
                                            isLastStep,
                                            user: selected_user,
                                            selected_device,
                                            all_devices,
                                            deviceOptions,
                                            maintenanceOptions
                                        }
                                    },
                                        deviceLayout,
                                        userPermissions
                                    )}
                                </SoftBox>
                            </Card>
                        </Form>
                    )
                }}
            </Formik>
        </SoftBox>
    );
}

InspectionsForm.propTypes = {
    selected_user: PropTypes.object.isRequired,
    all_devices: PropTypes.array.isRequired,
    selected_device: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
    onSubmit: PropTypes.func,
};

export default InspectionsForm;